import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import './Home.css';

function Home() {
  const [selectedRole, setSelectedRole] = useState('student'); // Default to student
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setError('');
    setUsername('');
    setPassword('');
  };


  // Check for saved credentials on component mount
  useEffect(() => {
    const checkSavedCredentials = async () => {
      const savedUsername = localStorage.getItem('studentRememberUsername');
      const savedPassword = localStorage.getItem('studentRememberPassword');
      
      if (savedUsername && savedPassword) {
        setUsername(savedUsername);
        setPassword(savedPassword);
        setRememberMe(true);
        
        // Auto-login with saved credentials
        try {
          await authenticateStudent(savedUsername, savedPassword);
        } catch (err) {
          console.log('Auto-login failed, showing login form');
        }
      }
    };

    checkSavedCredentials();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!username || !password) {
      setError(`Please enter both ${selectedRole === 'student' ? 'email' : 'username'} and password.`);
      setLoading(false);
      return;
    }

    try {
      if (selectedRole === 'student') {
        // Student authentication uses Supabase
        await authenticateStudent(username, password);
      } else if (selectedRole === 'parent') {
        // Parent authentication uses Supabase
        await authenticateParent(username, password);
      } else if (selectedRole === 'teacher') {
        // Teacher authentication uses Supabase
        await authenticateTeacher(username, password);
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(error.message || 'Failed to log in. Please try again.');
      setLoading(false);
    }
  };

  // Authenticate student using Supabase
  const authenticateStudent = async (email, password) => {
    try {
      console.log('Attempting student login with email:', email);
      setLoading(true);
      
      // First, find the student by email in the students table
      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select(`
          *,
          classes (
            id,
            class_name
          ),
          schools (
            id,
            school_name
          )
        `)
        .eq('email', email)
        .single();
      
      if (studentError || !studentData) {
        console.error('Error fetching student data:', studentError);
        throw new Error('Student profile not found. Please contact administrator.');
      }
      
      console.log('Student data found:', studentData);
      
      // If student has auth_user_id, try to authenticate with Supabase Auth
      if (studentData.auth_user_id) {
        try {
          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email: email,
            password: password
          });
          
          if (authError) {
            console.error('Auth error:', authError);
            throw new Error('Invalid email or password.');
          }
          
          console.log('Auth successful');
        } catch (authErr) {
          console.error('Auth failed, but student exists:', authErr);
          // Continue without auth - student exists in database
        }
      } else {
        // Student doesn't have auth account, just verify password matches
        // For now, we'll allow login if student exists (you can add password verification later)
        console.log('Student found without auth account, allowing login');
      }
      
      // Store student data in localStorage
      localStorage.setItem('studentData', JSON.stringify(studentData));
      localStorage.setItem('userType', 'student');
      
      // Save credentials if "Remember Me" is checked
      if (rememberMe) {
        localStorage.setItem('studentRememberUsername', username);
        localStorage.setItem('studentRememberPassword', password);
      } else {
        // Clear saved credentials if "Remember Me" is unchecked
        localStorage.removeItem('studentRememberUsername');
        localStorage.removeItem('studentRememberPassword');
      }
      
      // Navigate to student dashboard
      navigate('/student/dashboard');
      
    } catch (error) {
      console.error('Student authentication error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Authenticate parent using Supabase
  const authenticateParent = async (email, password) => {
    try {
      console.log('Attempting parent login for:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) {
        console.error('Parent login error:', error);
        throw new Error('Invalid parent credentials. Please check your email and password.');
      }

      if (data.user) {
        console.log('Parent login successful:', data.user);
        
        // Check if user is actually a parent (optional - use auth metadata if users table fails)
        let userData = null;
        
        try {
          const { data: existingUserData, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('id', data.user.id)
            .eq('user_type', 'parent')
            .single();

          if (!userError && existingUserData) {
            userData = existingUserData;
            console.log('Parent user found in users table:', userData);
          } else {
            console.log('Parent user not found in users table, using auth metadata');
            // Use auth metadata instead
            userData = {
              id: data.user.id,
              email: data.user.email,
              first_name: data.user.user_metadata?.first_name || 'Parent',
              last_name: data.user.user_metadata?.last_name || 'User',
              user_type: 'parent',
              child_email: data.user.user_metadata?.child_email || null
            };
          }
        } catch (userErr) {
          console.warn('Error checking users table, using auth metadata:', userErr);
          // Use auth metadata as fallback
          userData = {
            id: data.user.id,
            email: data.user.email,
            first_name: data.user.user_metadata?.first_name || 'Parent',
            last_name: data.user.user_metadata?.last_name || 'User',
            user_type: 'parent',
            child_email: data.user.user_metadata?.child_email || null
          };
        }

        // Store parent authentication
        localStorage.setItem('parentAuthenticated', 'true');
        localStorage.setItem('parentData', JSON.stringify({
          id: data.user.id,
          email: data.user.email,
          first_name: userData.first_name,
          last_name: userData.last_name,
          child_email: userData.child_email
        }));
        localStorage.setItem('supabaseAuthToken', data.session?.access_token);

        // Redirect to parent dashboard (placeholder for now)
        navigate('/parent/dashboard');
      } else {
        throw new Error('Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Parent authentication error:', error);
      throw error;
    }
  };

  // Authenticate teacher using teachers table (not Supabase Auth)
  const authenticateTeacher = async (email, password) => {
    try {
      console.log('=== TEACHER LOGIN DEBUG START ===');
      console.log('Attempting teacher login for:', email);
      
      // Find teacher by email in teachers table
      const { data: teacherRecord, error: teacherError } = await supabase
        .from('teachers')
        .select('*')
        .eq('email', email.trim().toLowerCase())
        .eq('is_active', true)
        .single();

      if (teacherError || !teacherRecord) {
        console.error('❌ Teacher not found:', teacherError);
        throw new Error('Invalid teacher credentials. Please check your email and password.');
      }

      console.log('✅ Teacher found in database:', teacherRecord);

      // Verify password from password_hash in teachers table
      if (!teacherRecord.password_hash) {
        console.error('❌ Teacher has no password_hash set');
        throw new Error('Teacher account is not properly configured. Please contact administrator.');
      }

      // Decode and compare password (using Base64 like schools)
      const storedPasswordHash = atob(teacherRecord.password_hash); // Decode from Base64
      const providedPassword = password.trim();

      if (storedPasswordHash !== providedPassword) {
        console.error('❌ Password mismatch');
        throw new Error('Invalid teacher credentials. Please check your email and password.');
      }

      console.log('✅ Password verified successfully');
      console.log('Teacher ID:', teacherRecord.id);
      console.log('Teacher Name:', teacherRecord.first_name, teacherRecord.last_name);
      console.log('Teacher class_ids:', teacherRecord.class_ids);
      console.log('Teacher class_sections:', teacherRecord.class_sections);

      // Store the complete teacher record
      const completeTeacherData = {
        ...teacherRecord,
        id: teacherRecord.id, // Use teacher table ID
        email: teacherRecord.email,
        teacher_table_id: teacherRecord.id
      };

      console.log('Complete teacher data to store:', completeTeacherData);
      
      localStorage.setItem('teacherAuthenticated', 'true');
      localStorage.setItem('teacherData', JSON.stringify(completeTeacherData));
      
      console.log('✅ Stored complete teacher record in localStorage');
      console.log('=== TEACHER LOGIN DEBUG END ===');

      // Redirect to teacher dashboard
      navigate('/teacher/dashboard');
    } catch (error) {
      console.error('❌ Teacher authentication error:', error);
      throw error;
    }
  };

  return (
    <div className="home-container">
      <main className="home-main">
        <div className="login-container">
          {/* Nexy Logo */}
          <div className="nexy-logo-container">
            <img 
              src="https://gjamvfwvqlizginqdfhd.supabase.co/storage/v1/object/public/transport-uploads/WhatsApp%20Image%202025-11-21%20at%2011.57.28%20AM%20(1).jpeg"
              alt="Nexy Logo"
              className="nexy-logo"
            />
          </div>

          <div className="role-tabs">
            <div 
              className={`role-tab ${selectedRole === 'student' ? 'active' : ''}`} 
              onClick={() => handleRoleSelect('student')}
            >
              <div className="tab-content">
                <div className="tab-icon">👨‍🎓</div>
                <span>Student</span>
              </div>
            </div>
            <div 
              className={`role-tab ${selectedRole === 'parent' ? 'active' : ''}`} 
              onClick={() => handleRoleSelect('parent')}
            >
              <div className="tab-content">
                <div className="tab-icon">👨‍👩‍👧‍👦</div>
                <span>Parent</span>
              </div>
            </div>
            <div 
              className={`role-tab ${selectedRole === 'teacher' ? 'active' : ''}`} 
              onClick={() => handleRoleSelect('teacher')}
            >
              <div className="tab-content">
                <div className="tab-icon">👨‍🏫</div>
                <span>Teacher</span>
              </div>
            </div>
          </div>
          
          <div className="login-form-container">
            <h2>Login As {selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}</h2>
            
            {error && <div className="error-message">{error}</div>}
            
            <form className="login-form" onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="username">{selectedRole === 'student' ? 'Email' : 'Username'}</label>
                <input
                  type={selectedRole === 'student' ? 'email' : 'text'}
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={selectedRole === 'student' ? 'Enter your email' : 'Enter your username'}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>
              
              <div className="remember-me-container">
                <label className="remember-me-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="remember-me-checkbox"
                  />
                  <span className="remember-me-text">Remember me</span>
                </label>
              </div>
              
              <button 
                type="submit" 
                className="login-button"
                disabled={loading}
              >
                {loading ? `Logging in...` : `Login as ${selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}`}
              </button>
            </form>

            <div className="role-description">
              {selectedRole === 'student' && (
                <p>Login as a student to access your courses, assignments, and grades.</p>
              )}
              {selectedRole === 'parent' && (
                <p>Login as a parent to monitor your child's progress and communicate with teachers.</p>
              )}
              {selectedRole === 'teacher' && (
                <p>Login as a teacher to manage classes, assignments, and student evaluations.</p>
              )}
            </div>
            <div className="transporter-prompt">
              <p>Are you managing school transport?</p>
              <button
                type="button"
                className="transporter-login-btn"
                onClick={() => navigate('/transporter/login')}
              >
                Go to Transporter Login
              </button>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
}

export default Home; 